import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const COUNTRY_CODES = [
  { group: 'South Asia',  label: 'India (+91)',          value: '+91'  },
  { group: 'South Asia',  label: 'Sri Lanka (+94)',       value: '+94'  },
  { group: 'South Asia',  label: 'Bangladesh (+880)',     value: '+880' },
  { group: 'South Asia',  label: 'Nepal (+977)',          value: '+977' },
  { group: 'SE Asia',     label: 'Singapore (+65)',       value: '+65'  },
  { group: 'SE Asia',     label: 'Malaysia (+60)',        value: '+60'  },
  { group: 'SE Asia',     label: 'Thailand (+66)',        value: '+66'  },
  { group: 'SE Asia',     label: 'Indonesia (+62)',       value: '+62'  },
  { group: 'SE Asia',     label: 'Philippines (+63)',     value: '+63'  },
  { group: 'SE Asia',     label: 'Vietnam (+84)',         value: '+84'  },
  { group: 'Middle East', label: 'UAE (+971)',            value: '+971' },
  { group: 'Middle East', label: 'Saudi Arabia (+966)',   value: '+966' },
  { group: 'Middle East', label: 'Qatar (+974)',          value: '+974' },
  { group: 'Middle East', label: 'Bahrain (+973)',        value: '+973' },
  { group: 'Middle East', label: 'Oman (+968)',           value: '+968' },
  { group: 'Middle East', label: 'Kuwait (+965)',         value: '+965' },
  { group: 'Diaspora',    label: 'USA / Canada (+1)',     value: '+1'   },
  { group: 'Diaspora',    label: 'UK (+44)',              value: '+44'  },
  { group: 'Diaspora',    label: 'Australia (+61)',       value: '+61'  },
  { group: 'Diaspora',    label: 'New Zealand (+64)',     value: '+64'  },
];

const GROUPS = [...new Set(COUNTRY_CODES.map(c => c.group))];

const SoloPatientLoginPage = () => {
  const navigate                      = useNavigate();
  const [step, setStep]               = useState(1);
  const [mobile, setMobile]           = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [otp, setOtp]                 = useState('');
  const [error, setError]             = useState('');
  const [loading, setLoading]         = useState(false);

  const handleRequestOtp = async (method = 'sms') => {
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/otp/request', {
        mobile,
        countryCode: countryCode.replace('+', ''),
        method,
      });
      setStep(2);
    } catch (err) {
      const code = err.response?.data?.code;
      setError(code === 'USER_NOT_FOUND'
        ? 'No account found for this number. Book a session first.'
        : err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/otp/verify', {
        mobile,
        countryCode: countryCode.replace('+', ''),
        otp,
      });
      localStorage.setItem('pwc_token', res.data.token);
      localStorage.setItem('pwc_user', JSON.stringify(res.data.user));
      navigate('/solo-patient/dashboard');
    } catch (err) {
      const code = err.response?.data?.code;
      if      (code === 'OTP_EXPIRED')      setError('Code expired. Tap resend.');
      else if (code === 'OTP_INVALID')      setError('Incorrect code.');
      else if (code === 'OTP_MAX_ATTEMPTS') setError('Too many attempts. Request a new code.');
      else setError(err.response?.data?.message || 'Something went wrong.');
    } finally { setLoading(false); }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {step === 1 ? (
          <>
            <h2 style={styles.title}>Patient Login</h2>
            <p style={styles.subtitle}>Enter your mobile number to receive a login code.</p>
            {error && <div style={styles.error}>{error}</div>}
            <div style={styles.field}>
              <label style={styles.label}>Country</label>
              <select value={countryCode} onChange={e => setCountryCode(e.target.value)}
                style={styles.input}>
                {GROUPS.map(group => (
                  <optgroup key={group} label={group}>
                    {COUNTRY_CODES.filter(c => c.group === group).map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Mobile Number</label>
              <input
                type="tel" value={mobile} autoFocus required
                onChange={e => setMobile(e.target.value.replace(/\D/g, ''))}
                style={styles.input} placeholder="10-digit number"
              />
            </div>
            <button onClick={() => handleRequestOtp('sms')}
              disabled={loading || !mobile.trim()}
              style={(loading || !mobile.trim()) ? { ...styles.btn, opacity: 0.7 } : styles.btn}>
              {loading ? 'Sending...' : 'Send OTP'}
            </button>
            {countryCode === '+91' && (
              <button onClick={() => handleRequestOtp('voice')}
                disabled={loading || !mobile.trim()}
                style={(loading || !mobile.trim()) ? { ...styles.btnOutline, opacity: 0.7 } : styles.btnOutline}>
                Call me instead
              </button>
            )}
          </>
        ) : (
          <>
            <h2 style={styles.title}>Enter Code</h2>
            <p style={styles.subtitle}>A 6-digit code was sent to {countryCode} {mobile}.</p>
            {error && <div style={styles.error}>{error}</div>}
            <form onSubmit={handleVerifyOtp}>
              <div style={styles.field}>
                <label style={styles.label}>One-Time Code</label>
                <input
                  type="password" value={otp} required autoFocus
                  inputMode="numeric" maxLength={6}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  style={{ ...styles.input, letterSpacing: '8px', fontSize: '20px', textAlign: 'center' }}
                  placeholder="000000"
                />
              </div>
              <button type="submit" disabled={loading}
                style={loading ? { ...styles.btn, opacity: 0.7 } : styles.btn}>
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </form>
            <button style={styles.resend}
              onClick={() => { setStep(1); setOtp(''); setError(''); }}>
              Resend OTP
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #071e12 0%, #0a1f14 30%, #0c2617 60%, #071e12 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px', fontFamily: "'Open Sans', sans-serif"
  },
  card: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px', padding: '48px 40px', width: '100%', maxWidth: '420px',
    backdropFilter: 'blur(20px)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
  },
  title: {
    fontFamily: "'Raleway', sans-serif", fontSize: '22px', fontWeight: '800',
    color: '#fff', margin: '0 0 8px', letterSpacing: '0.5px'
  },
  subtitle: { fontSize: '13px', color: 'rgba(255,255,255,0.4)', margin: '0 0 28px' },
  error: {
    background: 'rgba(192,57,43,0.2)', border: '1px solid rgba(192,57,43,0.4)',
    borderRadius: '6px', padding: '12px 16px', color: '#e74c3c',
    fontSize: '13px', marginBottom: '20px'
  },
  field: { marginBottom: '18px' },
  label: {
    display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px',
    textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)',
    marginBottom: '7px', fontFamily: "'Raleway', sans-serif"
  },
  input: {
    width: '100%', padding: '12px 14px', background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)', borderRadius: '5px',
    color: '#fff', fontSize: '14px', outline: 'none',
    boxSizing: 'border-box', fontFamily: "'Open Sans', sans-serif"
  },
  btn: {
    width: '100%', padding: '13px', background: '#4a8f82', color: '#fff',
    border: 'none', borderRadius: '5px', fontFamily: "'Raleway', sans-serif",
    fontSize: '12px', fontWeight: '700', letterSpacing: '1.5px',
    textTransform: 'uppercase', cursor: 'pointer', marginTop: '8px'
  },
  btnOutline: {
    width: '100%', padding: '13px', background: 'transparent', color: '#4a8f82',
    border: '1px solid #4a8f82', borderRadius: '5px', fontFamily: "'Raleway', sans-serif",
    fontSize: '12px', fontWeight: '700', letterSpacing: '1.5px',
    textTransform: 'uppercase', cursor: 'pointer', marginTop: '10px'
  },
  resend: {
    display: 'block', margin: '20px auto 0', background: 'none', border: 'none',
    color: 'rgba(255,255,255,0.4)', fontSize: '13px', cursor: 'pointer',
    textDecoration: 'underline', fontFamily: "'Open Sans', sans-serif"
  }
};

export default SoloPatientLoginPage;
